import { forwardRef } from 'react';
import { clsx } from 'clsx';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  /** Accessible label for screen readers (required if button only contains an icon) */
  ariaLabel?: string;
  /** ID of the element that describes this button */
  ariaDescribedBy?: string;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'primary',
      size = 'md',
      isLoading = false,
      children,
      disabled,
      ariaLabel,
      ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    // Check if button only contains an icon (no text content)
    const hasTextContent = typeof children === 'string' || 
      (Array.isArray(children) && children.some(c => typeof c === 'string'));
    
    // Require aria-label for icon-only buttons
    if (!hasTextContent && !ariaLabel && process.env.NODE_ENV === 'development') {
      console.warn('Button: Icon-only buttons should have an aria-label for accessibility');
    }

    return (
      <button
        ref={ref}
        disabled={disabled || isLoading}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        aria-busy={isLoading}
        className={clsx(
          // Base styles with enhanced focus indicators for accessibility
          'inline-flex items-center justify-center font-medium rounded-lg transition-all',
          // Enhanced focus styles for better visibility
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950',
          // High contrast focus indicator
          'focus-visible:shadow-[0_0_0_4px_rgba(59,130,246,0.3)]',
          {
            'px-3 py-1.5 text-sm': size === 'sm',
            'px-4 py-2 text-base': size === 'md',
            'px-6 py-3 text-lg': size === 'lg',
          },
          {
            'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800':
              variant === 'primary',
            'bg-slate-700 text-white hover:bg-slate-600 active:bg-slate-800':
              variant === 'secondary',
            'border-2 border-blue-600 text-blue-400 hover:bg-blue-600/10':
              variant === 'outline',
            'text-slate-300 hover:text-white hover:bg-slate-800':
              variant === 'ghost',
          },
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className
        )}
        {...props}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span>Wird geladen...</span>
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
